import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useApolloClient } from '@apollo/client';

import ValidationForm from 'components/ui/ValidationForm';
import { VoucherInput } from 'components/ui/Inputs';
import { isValidRedeemVoucherReference } from 'utils/validators';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import AlertBox from 'components/ui/AlertBox';
import { BOOKING_DEPOSIT_AMOUNT_LESS_THAN_VOUCHER_BALANCE_ERROR_REASON, DISCOUNT_ERROR, DISCOUNT_TYPE } from 'constants/vouchers';
import { Errors } from '../../SplitPayment/SplitPayment.styles';
import { StyledVoucherError } from './VoucherPayment.styles';
import VALIDATE_VOUCHER from './queries/VALIDATE_VOUCHER';

const VoucherForm = ({ bookingReference, onSubmit, resetVoucher, isDepositPayment }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const client = useApolloClient();

  const handleCancel = () => {
    setError('');
    resetVoucher();
  };

  if (error) {
    const isBookingDepositAmountLessThanVoucherBalanceError = error === BOOKING_DEPOSIT_AMOUNT_LESS_THAN_VOUCHER_BALANCE_ERROR_REASON;
    const isDiscountError = error === DISCOUNT_ERROR;

    let messageId = 'payments.voucherError';

    if (isBookingDepositAmountLessThanVoucherBalanceError) {
      messageId = 'payments.bookingDepositAmountLessThanVoucherBalanceError';
    } else if (isDiscountError) {
      messageId = 'payments.discountError';
    }

    return (
      <StyledVoucherError>
        <AlertBox variant={AlertBox.variants.ERROR}>
          <FormattedMessage id={messageId} />
        </AlertBox>
        <CancelButton onClick={handleCancel} type="button">
          <FormattedMessage id="payments.cancel" />
        </CancelButton>
      </StyledVoucherError>
    );
  }

  return (
    <ValidationForm
      validators={{
        voucherCode: (values) => isValidRedeemVoucherReference(values.voucherCode)
      }}
      fields={{ voucherCode: '' }}
      validateInitialFields
    >
      {({ fields, onChange, hasInvalidInputs, touchedInputs, invalidInputs }) => (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              setLoading(true);
              const { data } = await client.query({
                query: VALIDATE_VOUCHER,
                variables: {
                  reference: fields.voucherCode,
                  bookingReference,
                  isDepositPayment
                },
                fetchPolicy: 'network-only'
              });

              if (!data.validateVoucher.isValid) {
                throw new Error(data.validateVoucher.reason);
              }

              if (data.validateVoucher?.voucher?.type === DISCOUNT_TYPE) {
                throw new Error(DISCOUNT_ERROR);
              }

              onSubmit(data.validateVoucher);
            } catch (err) {
              setError(err.message);
            } finally {
              setLoading(false);
            }
          }}
        >
          <Row span={16}>
            <Row span={10}>
              <VoucherInput
                inline
                message={<FormattedMessage id="payments.EvoucherReference" />}
                name="voucherCode"
                value={fields.voucherCode}
                type="text"
                onChange={onChange}
              />
              <Errors>
                {touchedInputs.voucherCode && invalidInputs.voucherCode && (
                  <FormattedMessage id="payments.redeemVoucherReferenceValidationError" />
                )}
              </Errors>
            </Row>
          </Row>
          <Row span={16}>
            <Row span={7}>
              <ConfirmButton
                loading={loading}
                disabled={!fields.voucherCode || hasInvalidInputs}
                type="submit"
                data-cy="check-voucher"
                variant="primary"
              >
                <FormattedMessage id="payments.checkVoucher" />
              </ConfirmButton>
            </Row>
          </Row>
        </form>
      )}
    </ValidationForm>
  );
};

VoucherForm.propTypes = {
  bookingReference: PropTypes.string,
  onSubmit: PropTypes.func,
  resetVoucher: PropTypes.func,
  isDepositPayment: PropTypes.bool
};

export default VoucherForm;
